import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext'
import { mergeRegister } from '@lexical/utils'
import {
  $getSelection,
  $isRangeSelection,
  FORMAT_TEXT_COMMAND,
  SELECTION_CHANGE_COMMAND,
  $createParagraphNode,
} from 'lexical'
import {
  $isHeadingNode,
  $createHeadingNode,
  HeadingTagType,
  $createQuoteNode,
} from '@lexical/rich-text'
import {
  INSERT_UNORDERED_LIST_COMMAND,
  INSERT_ORDERED_LIST_COMMAND,
  $isListNode,
  REMOVE_LIST_COMMAND,
} from '@lexical/list'
import {
  FormatBold,
  FormatItalic,
  FormatUnderlined,
  FormatListBulleted,
  FormatListNumbered,
  StrikethroughS,
} from '@mui/icons-material'
import {
  Box,
  ToggleButton,
  ToggleButtonGroup,
  Select,
  MenuItem,
  FormControl,
  Divider,
  Tooltip,
} from '@mui/material'

export function TextBlockFloatingToolbar(): React.JSX.Element {
  let [editor] = useLexicalComposerContext()
  let toolbarRef = useRef<HTMLDivElement>(null)
  let [isVisible, setIsVisible] = useState(false)
  let [activeFormats, setActiveFormats] = useState<string[]>([])
  let [blockType, setBlockType] = useState('paragraph')
  let [activeListType, setActiveListType] = useState<
    'bullet' | 'number' | null
  >(null)
  let [position, setPosition] = useState({ top: 0, left: 0 })

  let updateToolbar = useCallback(() => {
    let selection = $getSelection()
    let nativeSelection = window.getSelection()

    // Check selection state for toolbar visibility
    if (
      selection !== null &&
      $isRangeSelection(selection) &&
      nativeSelection !== null &&
      !nativeSelection.isCollapsed &&
      nativeSelection.rangeCount > 0
    ) {
      let range = nativeSelection.getRangeAt(0)
      let rect = range.getBoundingClientRect()

      // Get editor container position to calculate relative positioning
      let editorContainer = editor.getRootElement()?.closest('.MuiBox-root')
      let containerRect = editorContainer?.getBoundingClientRect()

      // Position toolbar above selection
      setIsVisible(true)

      // Update active formats
      let formats = [
        ...(selection.hasFormat('bold') ? ['bold'] : []),
        ...(selection.hasFormat('italic') ? ['italic'] : []),
        ...(selection.hasFormat('underline') ? ['underline'] : []),
        ...(selection.hasFormat('strikethrough') ? ['strikethrough'] : []),
      ]
      setActiveFormats(formats)

      // Update block type and list state
      let anchorNode = selection.anchor.getNode()
      let element =
        anchorNode.getKey() === 'root'
          ? anchorNode
          : anchorNode.getTopLevelElementOrThrow()

      // Check if we're in a list using recursion
      let findListNode = (node: any): any => {
        if (!node) return null
        if ($isListNode(node)) return node
        return findListNode(node.getParent())
      }

      let listNode = findListNode(anchorNode)

      if (listNode) {
        setActiveListType(
          listNode.getListType() === 'bullet' ? 'bullet' : 'number',
        )
      } else {
        setActiveListType(null)
      }

      if ($isHeadingNode(element)) {
        setBlockType(element.getTag())
      } else if (element.getType() === 'quote') {
        setBlockType('quote')
      } else {
        setBlockType('paragraph')
      }

      // Position toolbar above selection, always centered in container
      let containerWidth = containerRect?.width || 300
      setPosition({
        top: rect.top - (containerRect?.top || 0) - 50,
        left: containerWidth / 2 - 150, // Always center the toolbar (300px width / 2 = 150px offset)
      })
    } else {
      setIsVisible(false)
    }
  }, [])

  useEffect(
    () =>
      mergeRegister(
        editor.registerUpdateListener(({ editorState }) =>
          editorState.read(updateToolbar),
        ),
        editor.registerCommand(SELECTION_CHANGE_COMMAND, () => false, 1),
      ),
    [editor, updateToolbar],
  )

  let handleFormatChange = (
    event: React.MouseEvent<HTMLElement>,
    newFormats: string[],
  ) => {
    // Find what changed
    let added = newFormats.find((f) => !activeFormats.includes(f))
    let removed = activeFormats.find((f) => !newFormats.includes(f))

    if (added) {
      editor.dispatchCommand(
        FORMAT_TEXT_COMMAND,
        added as 'bold' | 'italic' | 'underline' | 'strikethrough',
      )
    }
    if (removed) {
      editor.dispatchCommand(
        FORMAT_TEXT_COMMAND,
        removed as 'bold' | 'italic' | 'underline' | 'strikethrough',
      )
    }
  }

  let handleBlockTypeChange = (event: any) => {
    let newBlockType = event.target.value
    editor.update(() => {
      let selection = $getSelection()
      if (!$isRangeSelection(selection)) return

      let anchorNode = selection.anchor.getNode()
      let element: any =
        anchorNode.getKey() === 'root'
          ? anchorNode
          : anchorNode.getTopLevelElementOrThrow()

      if (newBlockType === 'paragraph') {
        // Convert to paragraph
        let paragraphNode = $createParagraphNode()
        paragraphNode.append(...element.getChildren())
        element.replace(paragraphNode)
      } else if (newBlockType === 'quote') {
        // Convert to quote
        let quoteNode = $createQuoteNode()
        quoteNode.append(...element.getChildren())
        element.replace(quoteNode)
      } else if (newBlockType.startsWith('h')) {
        // Create heading node
        let headingNode = $createHeadingNode(newBlockType as HeadingTagType)
        headingNode.append(...element.getChildren())
        element.replace(headingNode)
      }
    })
  }

  let handleListCommand = (listType: 'bullet' | 'number') => {
    // If we're already in the same list type, remove the list
    if (activeListType === listType) {
      editor.dispatchCommand(REMOVE_LIST_COMMAND, undefined)
    } else {
      // Check if we're currently in a heading and need to convert it to a list
      editor.update(() => {
        let selection = $getSelection()
        if (!$isRangeSelection(selection)) return

        let anchorNode = selection.anchor.getNode()
        let element =
          anchorNode.getKey() === 'root'
            ? anchorNode
            : anchorNode.getTopLevelElementOrThrow()

        // If we're converting from a heading, first convert to paragraph then to list
        if ($isHeadingNode(element)) {
          let paragraphNode = $createParagraphNode()
          paragraphNode.append(...element.getChildren())
          element.replace(paragraphNode)
        }
      })

      // Now create the list
      if (listType === 'bullet') {
        editor.dispatchCommand(INSERT_UNORDERED_LIST_COMMAND, undefined)
      } else {
        editor.dispatchCommand(INSERT_ORDERED_LIST_COMMAND, undefined)
      }
    }
  }

  if (!isVisible) {
    return <></>
  }

  return (
    <Box
      ref={toolbarRef}
      sx={{
        position: 'absolute',
        top: `${position.top}px`,
        left: `${position.left}px`,
        zIndex: 1000,
        backgroundColor: 'background.paper',
        border: 1,
        borderColor: 'divider',
        borderRadius: '8px',
        boxShadow: 3,
        p: 1,
        display: 'flex',
        gap: 1,
        alignItems: 'center',
      }}
    >
      {/* Block Type Selector */}
      <Tooltip
        title={
          activeListType
            ? 'Remove list formatting first to change text type'
            : ''
        }
        placement="top"
        arrow
      >
        <FormControl size="small" sx={{ minWidth: 100 }}>
          <Select
            value={blockType}
            onChange={handleBlockTypeChange}
            disabled={!!activeListType}
            sx={{
              fontSize: '12px',
              height: '32px',
              ...(activeListType && {
                opacity: 0.5,
                cursor: 'not-allowed',
              }),
            }}
          >
            <MenuItem value="paragraph">Paragraph</MenuItem>
            <MenuItem value="h1">Heading 1</MenuItem>
            <MenuItem value="h2">Heading 2</MenuItem>
            <MenuItem value="h3">Heading 3</MenuItem>
            <MenuItem value="h4">Heading 4</MenuItem>
            <MenuItem value="h5">Heading 5</MenuItem>
            <MenuItem value="h6">Heading 6</MenuItem>
            <MenuItem value="quote">Block Quote</MenuItem>
          </Select>
        </FormControl>
      </Tooltip>

      <Divider orientation="vertical" flexItem />

      {/* Text Formatting */}
      <ToggleButtonGroup
        value={activeFormats}
        onChange={handleFormatChange}
        size="small"
        sx={{
          '& .MuiToggleButton-root': {
            border: 'none',
            borderRadius: '4px',
            minWidth: '32px',
            height: '32px',
          },
        }}
      >
        <ToggleButton value="bold" aria-label="bold">
          <FormatBold fontSize="small" />
        </ToggleButton>
        <ToggleButton value="italic" aria-label="italic">
          <FormatItalic fontSize="small" />
        </ToggleButton>
        <ToggleButton value="underline" aria-label="underline">
          <FormatUnderlined fontSize="small" />
        </ToggleButton>
        <ToggleButton value="strikethrough" aria-label="strikethrough">
          <StrikethroughS fontSize="small" />
        </ToggleButton>
      </ToggleButtonGroup>

      <Divider orientation="vertical" flexItem />

      {/* Lists */}
      <ToggleButtonGroup size="small" value={activeListType}>
        <ToggleButton
          value="bullet"
          onClick={() => handleListCommand('bullet')}
          aria-label="bullet list"
        >
          <FormatListBulleted fontSize="small" />
        </ToggleButton>
        <ToggleButton
          value="number"
          onClick={() => handleListCommand('number')}
          aria-label="numbered list"
        >
          <FormatListNumbered fontSize="small" />
        </ToggleButton>
      </ToggleButtonGroup>
    </Box>
  )
}
